import type { authClient } from "@/lib/auth-client";
import { queryKeys } from "@/lib/query-keys";
import type { AppRouter } from "@papier/api";
import { Toaster } from "@papier/ui/components/ui/sonner";
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { HeadContent, Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import type { TRPCOptionsProxy } from "@trpc/tanstack-react-query";

export interface RouterAppContext {
  trpc: TRPCOptionsProxy<AppRouter>;
  queryClient: QueryClient;
  authClient: typeof authClient;
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
  context: ({ context: { authClient } }) => ({
    authOptions: {
      session: queryOptions({
        queryKey: queryKeys.session(),
        staleTime: 1000 * 60, // 1 minute
        queryFn: async () => {
          const { data: session } = await authClient.getSession({
            query: {
              disableCookieCache: true,
            },
          });
          return session;
        },
      }),
      hasOrganizations: queryOptions({
        queryKey: queryKeys.hasOrganizations(),
        staleTime: 1000 * 60, // 1 minute
        queryFn: async () => {
          const { data: orgs } = await authClient.organization.list();
          return !!orgs?.length;
        },
      }),
    },
  }),
  component: RootComponent,
});

function RootComponent() {
  return (
    <>
      <HeadContent />
      <Outlet />
      <TanStackRouterDevtools position="bottom-right" />
      <ReactQueryDevtools buttonPosition="bottom-right" />
      <Toaster richColors closeButton />
    </>
  );
}
